import { Table } from "components";
import { detailsCellHeader } from "components/DetailsCellHeader";
import { formatProgressPercent } from "features/dt-plus-setup/lib/executionSteps";
import { ExecutionStatusChip } from "features/shared/components/ExecutionStatusChip";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { convertDatetimeStringToDate } from "utils/date";
import { valueOrAlternative } from "utils/namesManipulation";

import type { IfcPipelineJob } from "../../model/types";

type ExecutionHistoryTableProps = {
  className?: string;
  items: IfcPipelineJob[];
  onRowClick: (item: IfcPipelineJob) => void;
};

export const ExecutionHistoryTable = ({ className, items, onRowClick }: ExecutionHistoryTableProps) => {
  const { t } = useTranslation();

  const headers = useMemo(
    () => [
      {
        dataKey: "ifc_file_name",
        label: t("Execution.history.columns.fileName"),
        valueClassName: "font-ds-body-semibold-sm",
        valueTransformation: (row: IfcPipelineJob) => row.ifc_file_name,
      },
      {
        dataKey: "status",
        label: t("general.status"),
        valueTransformation: (row: IfcPipelineJob) => <ExecutionStatusChip status={row.status} />,
      },
      {
        dataKey: "progress_percent",
        label: t("general.progress"),
        valueClassName: "font-ds-body-regular-xs",
        valueTransformation: (row: IfcPipelineJob) => formatProgressPercent(row.progress_percent),
      },
      {
        dataKey: "current_step_name",
        label: t("Execution.history.columns.currentStep"),
        minWidth: "12rem",
        valueClassName: "font-ds-body-regular-xs",
        valueTransformation: (row: IfcPipelineJob) => valueOrAlternative(row.current_step_name),
      },
      {
        dataKey: "created_at",
        label: t("general.createdAt"),
        minWidth: "12rem",
        valueClassName: "font-ds-body-regular-xs text-lds-foreground-secondary",
        valueTransformation: (row: IfcPipelineJob) => convertDatetimeStringToDate(row.created_at),
      },
      {
        dataKey: "completed_at",
        label: t("general.completedAt"),
        minWidth: "12rem",
        valueClassName: "font-ds-body-regular-xs text-lds-foreground-secondary",
        valueTransformation: (row: IfcPipelineJob) => convertDatetimeStringToDate(row.completed_at),
      },
      {
        dataKey: "message",
        label: t("general.summary"),
        valueClassName: "font-ds-body-regular-xs  ",
        valueTransformation: (row: IfcPipelineJob) => (
          <div className="max-w-[30rem] truncate">{row.message ?? row.error_msg}</div>
        ),
      },
      detailsCellHeader<IfcPipelineJob>({}, onRowClick),
    ],
    [t, onRowClick]
  );

  return (
    <Table
      className={className}
      data={items}
      editable={() => false}
      headers={headers}
      idColumnName="id"
      onRowClick={(row: IfcPipelineJob) => onRowClick(row)}
      rowRemoveable={() => false}
    />
  );
};
